import { sortedClips } from '../timeline.js';
import { fmt } from '../render/escape.js';
/** Seconds the music takes to go down, and to come back. */
export const DUCK_RAMP = 0.25;
/** Sorted, with overlapping and touching intervals joined. */
export function mergeIntervals(list) {
    const sorted = list.filter((i) => i.end > i.start).sort((a, b) => a.start - b.start);
    const out = [];
    for (const i of sorted) {
        const last = out[out.length - 1];
        if (last && i.start <= last.end)
            last.end = Math.max(last.end, i.end);
        else
            out.push({ ...i });
    }
    return out;
}
/**
 * When speech is audible: every clip with sound on a track that is not
 * itself ducked -- the base track's own sound, a voiceover, an interview.
 * Muted tracks and clips do not count; neither does a clip whose file has no
 * sound at all.
 */
export function speechIntervals(timeline, hasAudio) {
    const found = [];
    for (const track of timeline.tracks) {
        if (track.duck || track.muted || track.gain === 0)
            continue;
        for (const clip of sortedClips(track)) {
            if (clip.muted || clip.gain === 0 || clip.source.kind !== 'asset')
                continue;
            if (!hasAudio(clip.source.assetId))
                continue;
            found.push({ start: clip.start, end: clip.start + clip.duration });
        }
    }
    return mergeIntervals(found);
}
/** The music's gain at `t`: 1 clear of speech, `level` under it, ramps between. */
export function duckGain(intervals, t, level, ramp = DUCK_RAMP) {
    let gain = 1;
    for (const { start, end } of intervals) {
        const into = Math.min((t - (start - ramp)) / ramp, (end + ramp - t) / ramp);
        const depth = Math.min(1, Math.max(0, into));
        gain = Math.min(gain, 1 - (1 - level) * depth);
    }
    return gain;
}
/**
 * The same envelope as an ffmpeg expression in `t`, for `volume=...:eval=frame`.
 * Intervals are in the clip's own time; the caller shifts them. Null when
 * there is no speech, so no filter is added for nothing.
 */
export function duckExpression(intervals, level, ramp = DUCK_RAMP) {
    if (intervals.length === 0 || level >= 1)
        return null;
    const r = fmt(ramp);
    const l = fmt(level);
    const terms = intervals.map(({ start, end }) => {
        const a = fmt(start - ramp);
        const b = fmt(end + ramp);
        return `(1-(1-${l})*clip(min((t-${a})/${r},(${b}-t)/${r}),0,1))`;
    });
    // ffmpeg's min takes two arguments, so several intervals nest.
    return terms.reduce((acc, term) => (acc ? `min(${acc},${term})` : term), '');
}
//# sourceMappingURL=duck.js.map